import React, { useState, useCallback } from "react";
import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  InputAdornment,
} from "@mui/material";
import { colors } from "./colors";
import { CloseIcon } from "./icons";
import IconButton from "./IconButton";

export interface TextFieldProps extends Omit<MuiTextFieldProps, "variant"> {
  error?: boolean;
  helperText?: string;
  onClear?: () => void;
  showClearButton?: boolean;
  inputHeight?: number;
}

const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      error = false,
      helperText,
      sx,
      onClear,
      showClearButton,
      inputHeight,
      value,
      inputProps,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        inputProps?.onFocus?.(e);
        setIsFocused(true);
      },
      [inputProps],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        inputProps?.onBlur?.(e);
        setIsFocused(false);
      },
      [inputProps],
    );

    const hasValue = Boolean(value);
    const showClearWhenFocused =
      showClearButton ?? (onClear !== undefined && hasValue);
    const shouldShowClear = showClearWhenFocused && isFocused;

    return (
      <MuiTextField
        {...props}
        ref={ref}
        value={value}
        error={error}
        helperText={helperText}
        inputProps={{
          ...inputProps,
          onFocus: handleFocus,
          onBlur: handleBlur,
        }}
        InputProps={{
          ...props.InputProps,
          endAdornment: shouldShowClear ? (
            <InputAdornment position="end" sx={{ mr: "8px" }}>
              <IconButton
                ariaLabel="Clear input"
                onClick={onClear}
                onMouseDown={(e) => e.preventDefault()}
                size={24}
                sx={{
                  padding: "4px",
                  color: colors.neutral.white,
                  backgroundColor: colors.input.clearButtonBg,
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : (
            props.InputProps?.endAdornment
          ),
        }}
        sx={{
          fontFamily: "Outfit, sans-serif",
          "& .MuiOutlinedInput-root": {
            backgroundColor: colors.input.background,
            color: colors.neutral.white,
            fontFamily: "Outfit, sans-serif",
            fontSize: "1rem",
            fontWeight: 400,
            borderRadius: "100px",
            padding: "0px 6px",
            ...(inputHeight != null && {
              height: inputHeight,
              minHeight: inputHeight,
            }),
            "& fieldset": {
              borderColor: error ? colors.error[400] : colors.neutral[900],
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: error ? colors.error[400] : colors.neutral[900],
              borderWidth: "1px",
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? colors.error[400] : colors.neutral[900],
              borderWidth: "1px",
            },
            "&.Mui-error fieldset": {
              borderColor: colors.error[400],
              borderWidth: "1px",
            },
          },
          "& .MuiInputLabel-root": {
            color: colors.neutral.white,
            fontFamily: "Outfit, sans-serif",
            "&.Mui-focused": {
              color: colors.neutral.white,
            },
            "&.Mui-error": {
              color: colors.error[400],
            },
          },
          "& .MuiFormHelperText-root": {
            color: colors.error[400],
            fontFamily: "Outfit, sans-serif",
            fontSize: "0.875rem",
            fontWeight: 400,
            marginTop: "8px",
            "&.Mui-error": {
              color: colors.error[400],
            },
          },
          ...sx,
        }}
      />
    );
  },
);

TextField.displayName = "TextField";

export default TextField;
